import { Component, type ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Copy } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: string
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: '' }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      errorInfo: errorInfo.componentStack ?? '',
    })
    console.error('[ErrorBoundary]', error, errorInfo)
  }

  handleCopy = () => {
    const text = `${this.state.error?.message}\n\n${this.state.error?.stack}\n\nComponent Stack:${this.state.errorInfo}`
    navigator.clipboard.writeText(text)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-[400px] flex items-center justify-center p-8">
          <div className="max-w-2xl w-full bg-white border border-red-500/30 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-400" strokeWidth={1.25} />
              </div>
              <div>
                <h2 className="text-lg font-bold text-[#1B2559]">Erreur de rendu</h2>
                <p className="text-sm text-[#8B8FA7]">Un composant a plante</p>
              </div>
            </div>

            <div className="bg-light-body rounded-xl p-4 mb-4 overflow-auto max-h-48">
              <p className="text-red-400 font-mono text-sm whitespace-pre-wrap">
                {this.state.error?.message}
              </p>
            </div>

            {this.state.error?.stack && (
              <details className="mb-4">
                <summary className="text-[#8B8FA7] text-sm cursor-pointer hover:text-[#1B2559] transition-colors">
                  Stack trace
                </summary>
                <div className="bg-light-body rounded-xl p-4 mt-2 overflow-auto max-h-64">
                  <pre className="text-[#555A73] font-mono text-xs whitespace-pre-wrap">
                    {this.state.error.stack}
                  </pre>
                </div>
              </details>
            )}

            {this.state.errorInfo && (
              <details className="mb-6">
                <summary className="text-[#8B8FA7] text-sm cursor-pointer hover:text-[#1B2559] transition-colors">
                  Component stack
                </summary>
                <div className="bg-light-body rounded-xl p-4 mt-2 overflow-auto max-h-64">
                  <pre className="text-[#555A73] font-mono text-xs whitespace-pre-wrap">
                    {this.state.errorInfo}
                  </pre>
                </div>
              </details>
            )}

            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
              >
                <RefreshCw className="w-4 h-4" strokeWidth={1.25} />
                Recharger
              </button>
              <button
                onClick={this.handleCopy}
                className="flex items-center gap-2 px-4 py-2.5 bg-[#F4F5FA] border border-[#E2E4ED] text-[#8B8FA7] text-sm font-medium rounded-xl hover:text-[#1B2559] hover:border-[#C8CBD8] transition-colors"
              >
                <Copy className="w-4 h-4" strokeWidth={1.25} />
                Copier l'erreur
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
